import React, { useEffect, useRef, useState } from "react";

import {
  faArrowDown,
  faArrowsUpDown,
  faArrowUp,
} from "@fortawesome/pro-regular-svg-icons";
import { Column, HeaderContext } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";
import { useDebouncedCallback } from "use-debounce";

import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { Icon } from "src/design-system/Icon";

export type FilterOption = {
  value: string;
  label: string;
};

type SortableFilterHeaderProps<TData> = {
  info: HeaderContext<TData, unknown>;
  children: React.ReactNode;
  showFilter?: boolean;
  filterOptions?: FilterOption[];
};

// Debounced so large datasets don't re-filter on every keystroke.
const DebouncedFilterInput = <TData,>({
  column,
}: {
  column: Column<TData, unknown>;
}) => {
  const externalValue = (column.getFilterValue() as string) ?? "";
  const [value, setValue] = useState(externalValue);
  const lastPushedRef = useRef(externalValue);

  const pushFilter = useDebouncedCallback((next: string) => {
    lastPushedRef.current = next;
    column.setFilterValue(next || undefined);
  }, 250);

  // Sync back when the filter is changed externally (e.g. a reset) without
  // clobbering in-flight user typing. Cancel any pending push so a stale
  // typed value doesn't land after the external reset.
  useEffect(() => {
    if (externalValue !== lastPushedRef.current) {
      pushFilter.cancel();
      lastPushedRef.current = externalValue;
      setValue(externalValue);
    }
  }, [externalValue, pushFilter]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    pushFilter(e.target.value);
  };

  return (
    <Input
      placeholder="Filter..."
      type="text"
      value={value}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

export const SortableFilterHeader = <TData,>({
  info,
  children,
  showFilter = false,
  filterOptions,
}: SortableFilterHeaderProps<TData>) => {
  const column = info.column;
  const isSorted = column.getIsSorted();
  const canSort = column.getCanSort();
  const filterValue = (column.getFilterValue() as string) ?? "";
  let sortIcon = faArrowsUpDown;
  if (isSorted === "asc") {
    sortIcon = faArrowUp;
  } else if (isSorted === "desc") {
    sortIcon = faArrowDown;
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <button
        className={twJoin(
          "flex items-center gap-2 whitespace-nowrap text-left font-inter-medium-12px text-gray-800",
          canSort && "cursor-pointer hover:text-gray-900",
        )}
        disabled={!canSort}
        type="button"
        onClick={() => canSort && column.toggleSorting()}
      >
        <span>{children}</span>
        {canSort && <Icon icon={sortIcon} size="2xs" />}
      </button>
      {filterOptions && column.getCanFilter() && (
        <div className="min-w-[120px]" onClick={(e) => e.stopPropagation()}>
          <Select
            options={[
              { key: "", value: "All" },
              ...filterOptions.map(({ value, label }) => ({
                key: value,
                value: label,
              })),
            ]}
            placeholder="All"
            value={filterValue}
            fullWidth
            onChange={(key) => column.setFilterValue(key || undefined)}
          />
        </div>
      )}
      {showFilter && !filterOptions && column.getCanFilter() && (
        <div className="min-w-[120px]">
          <DebouncedFilterInput column={column} />
        </div>
      )}
    </div>
  );
};
